/**
 * @fileoverview PDFビューアダイアログコンポーネント
 * @description モバイル向けPDF.jsベースのPDFビューア（フルスクリーンダイアログ）
 * @module apps/web/components/pdf/pdf-viewer-dialog
 * @remarks
 * Android ChromeではPDFの#page=Nフラグメントがサポートされないため、
 * モバイル環境では自前のPDF.jsビューアで表示する。
 * デスクトップでは従来どおりブラウザのPDFビューアを使用。
 *
 * UI設計: docs/web_ui.pen の 7YQ8J ノード参照
 */

import type React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'

// PDF.js workerの設定
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.mjs'

// ============================================================================
// 型定義
// ============================================================================

/**
 * PDFビューアダイアログのprops型
 */
export interface PdfViewerDialogProps {
  /** ダイアログの開閉状態 */
  isOpen: boolean
  /** ダイアログを閉じるコールバック */
  onClose: () => void
  /** PDFのURL（MLIT公式URLなど） */
  pdfUrl: string
  /** 初期表示ページ番号 */
  initialPage: number
}

/**
 * PDFダイアログの状態管理用の型
 * @description source-card.tsxなどで使用する状態の型
 */
export interface PdfDialogState {
  /** ダイアログの開閉状態 */
  isOpen: boolean
  /** PDFのURL */
  pdfUrl: string
  /** 表示ページ番号 */
  page: number
}

// ============================================================================
// コンポーネント
// ============================================================================

/**
 * PDFビューアダイアログコンポーネント
 * @description フルスクリーンでPDFを表示するダイアログ
 * @remarks
 * - ×ボタンで閉じる（URLは変わらない）
 * - 前へ/次へボタンでページ移動
 * - ページ番号入力で直接ジャンプ
 *
 * @example
 * ```tsx
 * <PdfViewerDialog
 *   isOpen={pdfDialogState.isOpen}
 *   onClose={() => setPdfDialogState(prev => ({ ...prev, isOpen: false }))}
 *   pdfUrl={pdfDialogState.pdfUrl}
 *   initialPage={pdfDialogState.page}
 * />
 * ```
 */
export function PdfViewerDialog({ isOpen, onClose, pdfUrl, initialPage }: PdfViewerDialogProps) {
  // ============================================================================
  // 状態管理
  // ============================================================================

  /** 総ページ数 */
  const [numPages, setNumPages] = useState<number>(0)
  /** 現在のページ番号 */
  const [currentPage, setCurrentPage] = useState(initialPage)
  /** ページ入力フィールドの値（文字列として管理） */
  const [pageInput, setPageInput] = useState(String(initialPage))
  /** ローディング状態 */
  const [isLoading, setIsLoading] = useState(true)
  /** エラー状態 */
  const [error, setError] = useState<string | null>(null)

  // ============================================================================
  // プロキシURL生成
  // ============================================================================

  /**
   * プロキシ経由のPDF URL
   * @description CORS回避のため、APIプロキシ経由でPDFを取得
   */
  const proxyUrl = pdfUrl
    ? `${import.meta.env.VITE_API_BASE_URL || ''}/api/pdf-proxy?url=${encodeURIComponent(pdfUrl)}`
    : ''

  // ============================================================================
  // 副作用
  // ============================================================================

  /**
   * ダイアログが開かれたとき、またはinitialPageが変更されたら現在ページをリセット
   * @remarks
   * isOpenを依存配列に含めることで、ダイアログを閉じて再度開いたときに
   * 前回の閲覧位置ではなく指定されたページから表示される
   */
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(initialPage)
      setPageInput(String(initialPage))
    }
  }, [isOpen, initialPage])

  /**
   * ダイアログが開いたときにbodyのスクロールを無効化
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // ============================================================================
  // イベントハンドラー
  // ============================================================================

  /**
   * PDF読み込み成功時のコールバック
   */
  const handleDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages)
      setIsLoading(false)
      setError(null)
      // 初期ページが範囲外の場合は調整
      if (initialPage > numPages) {
        setCurrentPage(numPages)
        setPageInput(String(numPages))
      }
    },
    [initialPage]
  )

  /**
   * PDF読み込み失敗時のコールバック
   */
  const handleDocumentLoadError = useCallback((err: Error) => {
    console.error('PDF読み込みエラー:', err)
    setIsLoading(false)
    setError('PDFの読み込みに失敗しました')
  }, [])

  /**
   * 前のページへ移動
   */
  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => {
      const newPage = Math.max(1, prev - 1)
      setPageInput(String(newPage))
      return newPage
    })
  }, [])

  /**
   * 次のページへ移動
   */
  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => {
      const newPage = Math.min(numPages, prev + 1)
      setPageInput(String(newPage))
      return newPage
    })
  }, [numPages])

  /**
   * ページ入力フィールドの変更
   */
  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value)
  }, [])

  /**
   * ページ入力フィールドからフォーカスが外れたときにページ移動
   */
  const handlePageInputBlur = useCallback(() => {
    const parsed = parseInt(pageInput, 10)
    if (!isNaN(parsed) && parsed >= 1 && parsed <= numPages) {
      setCurrentPage(parsed)
    } else {
      // 無効な値の場合は現在のページに戻す
      setPageInput(String(currentPage))
    }
  }, [pageInput, numPages, currentPage])

  /**
   * Enterキーでページ移動
   */
  const handlePageInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handlePageInputBlur()
      }
    },
    [handlePageInputBlur]
  )

  // ============================================================================
  // レンダリング
  // ============================================================================

  // ダイアログが閉じている場合は何も表示しない
  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#F5F4F1] flex flex-col" data-testid="pdf-viewer-dialog">
      {/* ヘッダー */}
      <header className="flex items-center justify-between px-4 py-3 bg-white border-b border-[#E5E5E5]">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="閉じる"
            data-testid="pdf-close-button"
          >
            <X className="w-6 h-6 text-[#1A1918]" />
          </button>
          <span className="font-semibold text-[#1A1918]">PDFビューア</span>
        </div>
        <span className="text-sm text-gray-500" data-testid="pdf-current-page">
          {currentPage} / {numPages || '...'}
        </span>
      </header>

      {/* PDFコンテンツ */}
      <div className="flex-1 flex items-center justify-center overflow-auto p-4">
        {isLoading && (
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">PDFを読み込み中...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-2 text-red-500">
            <span className="text-sm">{error}</span>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              閉じる
            </button>
          </div>
        )}

        {proxyUrl && (
          <Document
            file={proxyUrl}
            onLoadSuccess={handleDocumentLoadSuccess}
            onLoadError={handleDocumentLoadError}
            loading={null}
            className={isLoading ? 'hidden' : 'shadow-lg rounded-lg overflow-hidden'}
          >
            <Page
              pageNumber={currentPage}
              width={Math.min(380, window.innerWidth - 32)}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        )}
      </div>

      {/* 下部ナビゲーション */}
      <footer className="flex items-center justify-between px-4 py-3 bg-white border-t border-[#E5E5E5]">
        {/* 前へボタン */}
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={currentPage <= 1}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F0F0F0] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E5E5E5] transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-[#1A1918]" />
          <span className="text-sm font-medium text-[#1A1918]">前へ</span>
        </button>

        {/* ページインジケーター */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pageInput}
            onChange={handlePageInputChange}
            onBlur={handlePageInputBlur}
            onKeyDown={handlePageInputKeyDown}
            className="w-12 text-center border border-[#CCCCCC] rounded-md py-1 text-sm font-medium"
            aria-label="ページ番号"
          />
          <span className="text-gray-500 text-sm">/ {numPages || '...'}</span>
        </div>

        {/* 次へボタン */}
        <button
          type="button"
          onClick={handleNextPage}
          disabled={currentPage >= numPages}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1A1918] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#333] transition-colors"
        >
          <span className="text-sm font-medium text-white">次へ</span>
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </footer>
    </div>
  )
}
